import * as React from "react";
import {
  Stack,
  Box,
  Button,
  Chip,
  Typography,
  Modal,
  Paper,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Sidebar from "../Dashboard/Sidebar.js";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { ToastContainer, toast } from "react-toastify";
import { pushSubscriptionService } from "../../services/pushSubscriptionService";

// Icons
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import SendIcon from "@mui/icons-material/Send";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonIcon from "@mui/icons-material/Person";
import DevicesIcon from "@mui/icons-material/Devices";
import RefreshIcon from "@mui/icons-material/Refresh";

// --- STYLED COMPONENTS ---

const RootContainer = styled(Box)({
  background: "#F8FAFC",
  minHeight: "100vh",
  display: "flex",
});

const ContentArea = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  marginLeft: "18vw",
  padding: "40px",
  width: "calc(100% - 18vw)",
  transition: "all 0.3s ease",
  [theme.breakpoints.down("md")]: {
    marginLeft: 0,
    width: "100%",
    padding: "20px",
    paddingTop: "84px",
  },
}));

const GlassCard = styled(Paper)({
  padding: "24px",
  borderRadius: "16px",
  background: "#FFFFFF",
  border: "1px solid #E2E8F0",
  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
});

const StyledDataGrid = styled(DataGrid)({
  border: "none",
  backgroundColor: "#FFFFFF",
  borderRadius: "16px",
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#F1F5F9",
    color: "#475569",
    fontWeight: 700,
    fontSize: "14px",
    borderBottom: "1px solid #E2E8F0",
  },
  "& .MuiDataGrid-cell": {
    borderBottom: "1px solid #F1F5F9",
    color: "#1E293B",
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: "1px solid #E2E8F0",
  },
});

const StatChip = styled(Box)(({ color }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "12px 18px",
  borderRadius: "12px",
  backgroundColor: `${color}15`,
  color: color,
  fontWeight: 600,
  fontSize: "0.9rem",
}));

const ModalBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "500px",
  maxHeight: "90vh",
  overflowY: "auto",
  backgroundColor: "#FFFFFF",
  borderRadius: "20px",
  boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
  outline: "none",
  [theme.breakpoints.down("sm")]: {
    width: "95%",
  },
}));

const SALES_EXECUTIVES = [
  "Riyaz Ahmed",
  "Adil Kamal",
  "Biswadeep Roy",
  "Syed Kamran Hassan",
  "Saad Ahmed",
  "Saif Ahmed",
  "Subham Das",
  "Jaspreetraj Singh",
];

const PushNotifications = () => {
  const [subscriptions, setSubscriptions] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);

  // Test push modal
  const [testOpen, setTestOpen] = React.useState(false);
  const [testUsername, setTestUsername] = React.useState("");
  const [testTitle, setTestTitle] = React.useState("");
  const [testBody, setTestBody] = React.useState("");
  const [testSending, setTestSending] = React.useState(false);

  const fetchSubscriptions = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await pushSubscriptionService.getSubscriptions();
      if (result.success) {
        const list = Array.isArray(result.data)
          ? result.data
          : Array.isArray(result.data?.data)
            ? result.data.data
            : [];
        setSubscriptions(list);
      } else {
        toast.error(result.message || "Failed to load subscriptions");
        setSubscriptions([]);
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to load subscriptions");
    }
    setIsLoading(false);
  }, []);

  React.useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  // Unique users count
  const uniqueUsers = [...new Set(subscriptions.map((s) => s.username).filter(Boolean))];

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this push subscription?")) return;
    try {
      const result = await pushSubscriptionService.deleteSubscription(id);
      if (result.success) {
        toast.success("Subscription removed");
        fetchSubscriptions();
      } else {
        toast.error(result.message || "Failed to delete");
      }
    } catch (e) {
      toast.error("Failed to delete subscription");
    }
  };

  const handleTestOpen = () => {
    setTestUsername("");
    setTestTitle("");
    setTestBody("");
    setTestOpen(true);
  };

  const handleTestSend = async () => {
    if (!testUsername) {
      toast.warn("Please select a user");
      return;
    }
    if (!testTitle.trim()) {
      toast.warn("Please enter a title");
      return;
    }
    if (!testBody.trim()) {
      toast.warn("Please enter a message body");
      return;
    }

    setTestSending(true);
    try {
      const result = await pushSubscriptionService.sendTestPush(
        testUsername,
        testTitle.trim(),
        testBody.trim()
      );
      if (result.success) {
        toast.success("Test notification sent!");
        setTestOpen(false);
      } else {
        toast.error(result.message || "Failed to send notification");
      }
    } catch (e) {
      toast.error("Failed to send notification");
    }
    setTestSending(false);
  };

  const columns = [
    {
      field: "username",
      headerName: "User",
      width: 200,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <PersonIcon sx={{ fontSize: 18, color: "#64748B" }} />
          <Typography sx={{ fontSize: "0.9rem", fontWeight: 600 }}>
            {params.value || "Unknown"}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "subscription",
      headerName: "Endpoint",
      flex: 1,
      minWidth: 300,
      renderCell: (params) => {
        const row = params.row;
        // Handle different data shapes: subscription.endpoint, endpoint, or nested
        const sub = row.subscription;
        const endpoint =
          (typeof sub === "object" && sub?.endpoint) ||
          row.endpoint ||
          (typeof sub === "string" ? sub : "") ||
          "";
        const short = endpoint.length > 60 ? endpoint.slice(0, 60) + "..." : endpoint;
        return (
          <Tooltip title={endpoint}>
            <Typography
              sx={{
                fontSize: "0.8rem",
                color: "#64748B",
                fontFamily: "monospace",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {short || "-"}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: "created_at",
      headerName: "Subscribed",
      width: 180,
      renderCell: (params) => {
        const date = params.value ? new Date(params.value).toLocaleString() : "-";
        return (
          <Typography sx={{ fontSize: "0.85rem", color: "#64748B" }}>
            {date}
          </Typography>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const row = params.row;
        return (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title="Send test notification">
              <IconButton
                size="small"
                onClick={() => {
                  setTestUsername(row.username || "");
                  setTestTitle("Test Notification");
                  setTestBody("");
                  setTestOpen(true);
                }}
                sx={{
                  color: "#3B82F6",
                  "&:hover": { backgroundColor: "#DBEAFE" },
                }}
              >
                <SendIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Remove subscription">
              <IconButton
                size="small"
                onClick={() => handleDelete(row.id || row._id)}
                sx={{
                  color: "#EF4444",
                  "&:hover": { backgroundColor: "#FEE2E2" },
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        );
      },
    },
  ];

  return (
    <RootContainer>
      <Sidebar />
      <ContentArea>
        {/* HEADER */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={3}
          sx={{ mb: 4 }}
        >
          <Box>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: "#1E293B",
                fontFamily: "Kamerik",
                letterSpacing: "-1.5px",
                fontSize: { xs: "2rem", md: "3rem" },
              }}
            >
              Push Notifications
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748B", fontWeight: 500 }}>
              Manage push subscriptions and send test notifications
            </Typography>
          </Box>

          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchSubscriptions}
              sx={{
                borderRadius: "12px",
                px: 3,
                color: "#64748B",
                borderColor: "#E2E8F0",
                fontWeight: 600,
                "&:hover": { borderColor: "#CBD5E1", backgroundColor: "#F8FAFC" },
              }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={handleTestOpen}
              sx={{
                borderRadius: "12px",
                px: 3,
                bgcolor: "#1E293B",
                "&:hover": { bgcolor: "#0F172A" },
              }}
            >
              Send Test Push
            </Button>
          </Stack>
        </Stack>

        {/* STATS */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ mb: 4, flexWrap: "wrap" }}
        >
          <StatChip color="#3B82F6">
            <DevicesIcon fontSize="small" />
            Total Subscriptions: {subscriptions.length}
          </StatChip>
          <StatChip color="#10B981">
            <PersonIcon fontSize="small" />
            Unique Users: {uniqueUsers.length}
          </StatChip>
        </Stack>

        {/* DATA GRID */}
        <GlassCard elevation={0}>
          <Box sx={{ height: "60vh", width: "100%" }}>
            <StyledDataGrid
              rows={subscriptions}
              columns={columns}
              getRowId={(row) => row.id || row._id}
              loading={isLoading}
              disableSelectionOnClick
              components={{ Toolbar: GridToolbar }}
              componentsProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                },
              }}
              pageSize={20}
              rowsPerPageOptions={[10, 20, 50]}
              sx={{
                "& .MuiDataGrid-toolbarContainer": {
                  padding: "16px",
                  gap: "12px",
                  borderBottom: "1px solid #E2E8F0",
                  "& .MuiButton-root": {
                    color: "#64748B",
                    fontWeight: 700,
                    "&:hover": { color: "#1E293B" },
                  },
                },
              }}
            />
          </Box>
        </GlassCard>
      </ContentArea>

      {/* TEST PUSH MODAL */}
      <Modal open={testOpen} onClose={() => setTestOpen(false)}>
        <ModalBox>
          <Box
            sx={{
              p: 3,
              background: "linear-gradient(135deg, #1E293B 0%, #0F172A 100%)",
              color: "#fff",
              borderTopLeftRadius: "20px",
              borderTopRightRadius: "20px",
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1 }}
            >
              <NotificationsActiveIcon /> Send Test Notification
            </Typography>
          </Box>

          <Box sx={{ p: 4 }}>
            <Stack spacing={2.5}>
              <FormControl fullWidth>
                <InputLabel>Send To *</InputLabel>
                <Select
                  value={testUsername}
                  label="Send To *"
                  onChange={(e) => setTestUsername(e.target.value)}
                  sx={{ borderRadius: "12px" }}
                >
                  {SALES_EXECUTIVES.map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Title *"
                value={testTitle}
                onChange={(e) => setTestTitle(e.target.value)}
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
              />

              <TextField
                label="Message *"
                value={testBody}
                onChange={(e) => setTestBody(e.target.value)}
                multiline
                rows={3}
                fullWidth
                placeholder="Enter notification message..."
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
              />

              <Button
                fullWidth
                variant="contained"
                onClick={handleTestSend}
                disabled={testSending}
                startIcon={testSending ? null : <SendIcon />}
                sx={{
                  borderRadius: "12px",
                  py: 1.8,
                  bgcolor: "#eb1d27",
                  fontWeight: 700,
                  fontSize: "1rem",
                  "&:hover": { bgcolor: "#9e1c29" },
                }}
              >
                {testSending ? (
                  <CircularProgress size={24} sx={{ color: "#fff" }} />
                ) : (
                  "Send Notification"
                )}
              </Button>
            </Stack>
          </Box>
        </ModalBox>
      </Modal>

      <ToastContainer position="bottom-right" />
    </RootContainer>
  );
};

export default PushNotifications;
